/**
 * Cookie utility functions to replace localStorage
 * All cookies are set with secure flags and appropriate expiration times
 */

export type FlowType = 'customer' | 'distributor';

export interface CookieOptions {
  expires?: number; // Days until expiration
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

/**
 * Get the current flow type based on URL pathname
 * @returns 'customer' if path starts with '/customer', otherwise 'distributor'
 */
export const getCurrentFlow = (): FlowType => {
  if (typeof window !== 'undefined') {
    return window.location.pathname.startsWith('/customer') ? 'customer' : 'distributor';
  }
  return 'distributor';
};

/**
 * Get flow-specific cookie name by prefixing base name with flow type
 * @param baseName - Base cookie name (e.g., 'token', 'userId')
 * @param flow - Optional flow type. If not provided, uses current flow from URL
 * @returns Flow-prefixed cookie name (e.g., 'customer_token', 'distributor_userId')
 */
export const getFlowCookieName = (baseName: string, flow?: FlowType): string => {
  const flowType = flow || getCurrentFlow();
  return `${flowType}_${baseName}`;
};

/**
 * Get a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');
  
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  
  return null;
};

/**
 * Set a cookie with options
 * @param name - Cookie name
 * @param value - Cookie value
 * @param options - Cookie options (expires, path, domain, secure, sameSite)
 */
export const setCookie = (
  name: string,
  value: string,
  options: CookieOptions = {}
): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const {
    expires = 7, // Default 7 days
    path = '/',
    domain,
    secure = window.location.protocol === 'https:',
    sameSite = 'Lax',
  } = options;

  let cookieString = `${name}=${encodeURIComponent(value)}`;

  // Add expiration
  if (expires) {
    const date = new Date();
    date.setTime(date.getTime() + expires * 24 * 60 * 60 * 1000);
    cookieString += `; expires=${date.toUTCString()}`;
  }

  // Add path
  cookieString += `; path=${path}`;

  // Add domain if specified
  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  // Add secure flag
  if (secure) {
    cookieString += '; secure';
  }

  // Add SameSite
  cookieString += `; SameSite=${sameSite}`;

  document.cookie = cookieString;
};

/**
 * Remove a cookie by name
 * @param name - Cookie name
 * @param options - Cookie options (path, domain)
 */
export const removeCookie = (
  name: string,
  options: Pick<CookieOptions, 'path' | 'domain'> = {}
): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const { path = '/', domain } = options;

  let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  document.cookie = cookieString;
};

/**
 * Clear all cookies
 * Note: This only clears cookies accessible from the current path and domain
 */
export const clearAllCookies = (): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
    
    if (name) {
      removeCookie(name);
      // Also try to remove with different path combinations
      removeCookie(name, { path: '/' });
      removeCookie(name, { path: '' });
    }
  }
};

/**
 * Check if a cookie exists
 * @param name - Cookie name
 * @returns true if cookie exists, false otherwise
 */
export const hasCookie = (name: string): boolean => {
  return getCookie(name) !== null;
};

/**
 * Get all cookies as an object
 * @returns Object with cookie names as keys and values as values
 */
export const getAllCookies = (): Record<string, string> => {
  if (typeof document === 'undefined') {
    return {};
  }

  const cookies: Record<string, string> = {};
  const cookieStrings = document.cookie.split(';');

  for (let i = 0; i < cookieStrings.length; i++) {
    const cookie = cookieStrings[i].trim();
    const eqPos = cookie.indexOf('=');
    
    if (eqPos > -1) {
      const name = cookie.substring(0, eqPos).trim();
      const value = decodeURIComponent(cookie.substring(eqPos + 1));
      cookies[name] = value;
    }
  }

  return cookies;
};

/**
 * Base cookie names that will be prefixed with flow type
 * These are the base names used internally before flow prefixing
 */
export const COOKIE_BASE_NAMES = {
  TOKEN: 'token',
  USER_ID: 'userId',
  ROLE: 'role',
  SELECTED_LANGUAGE: 'selectedLanguage',
  COOKIE_CONSENT: 'cookieConsent',
} as const;

/**
 * Helper functions for flow-specific cookies
 * All functions support an optional flow parameter. If not provided, the current flow is determined from the URL.
 * 
 * @example
 * // Get token for current flow
 * const token = cookieHelpers.getToken();
 * 
 * // Get token for specific flow
 * const distributorToken = cookieHelpers.getToken('distributor');
 */
export const cookieHelpers = {
  /**
   * Get authentication token for the specified flow
   * @param flow - Optional flow type. Defaults to current flow from URL
   * @returns Token string or null if not found
   */
  getToken: (flow?: FlowType) => getCookie(getFlowCookieName(COOKIE_BASE_NAMES.TOKEN, flow)),
  
  /**
   * Set authentication token for the specified flow
   * @param value - Token value to store
   * @param flow - Optional flow type. Defaults to current flow from URL
   */
  setToken: (value: string, flow?: FlowType) => setCookie(getFlowCookieName(COOKIE_BASE_NAMES.TOKEN, flow), value, { expires: 2 }),
  
  /**
   * Remove authentication token for the specified flow
   * @param flow - Optional flow type. Defaults to current flow from URL
   */
  removeToken: (flow?: FlowType) => removeCookie(getFlowCookieName(COOKIE_BASE_NAMES.TOKEN, flow)),

  /**
   * Get user ID for the specified flow
   * @param flow - Optional flow type. Defaults to current flow from URL
   * @returns User ID string or null if not found
   */
  getUserId: (flow?: FlowType) => getCookie(getFlowCookieName(COOKIE_BASE_NAMES.USER_ID, flow)),
  
  /**
   * Set user ID for the specified flow
   * @param value - User ID value to store
   * @param flow - Optional flow type. Defaults to current flow from URL
   */
  setUserId: (value: string, flow?: FlowType) => setCookie(getFlowCookieName(COOKIE_BASE_NAMES.USER_ID, flow), value, { expires: 2 }),
  
  /**
   * Remove user ID for the specified flow
   * @param flow - Optional flow type. Defaults to current flow from URL
   */
  removeUserId: (flow?: FlowType) => removeCookie(getFlowCookieName(COOKIE_BASE_NAMES.USER_ID, flow)),

  /**
   * Get user role for the specified flow
   * @param flow - Optional flow type. Defaults to current flow from URL
   * @returns Role as 1 (distributor) or 2 (customer), or null if not found
   */
  getRole: (flow?: FlowType) => {
    const role = getCookie(getFlowCookieName(COOKIE_BASE_NAMES.ROLE, flow));
    return role ? parseInt(role) as 1 | 2 : null;
  },
  
  /**
   * Set user role for the specified flow
   * @param value - Role value (1 for distributor, 2 for customer)
   * @param flow - Optional flow type. Defaults to current flow from URL
   */
  setRole: (value: 1 | 2, flow?: FlowType) => setCookie(getFlowCookieName(COOKIE_BASE_NAMES.ROLE, flow), value.toString(), { expires: 2 }),
  
  /**
   * Remove user role for the specified flow
   * @param flow - Optional flow type. Defaults to current flow from URL
   */
  removeRole: (flow?: FlowType) => removeCookie(getFlowCookieName(COOKIE_BASE_NAMES.ROLE, flow)),

  /**
   * Get selected language for the specified flow
   * @param flow - Optional flow type. Defaults to current flow from URL
   * @returns Language code string, defaults to 'en' if not found
   */
  getLanguage: (flow?: FlowType) => getCookie(getFlowCookieName(COOKIE_BASE_NAMES.SELECTED_LANGUAGE, flow)) || 'en',
  
  /**
   * Set selected language for the specified flow
   * @param value - Language code to store (e.g., 'en', 'es', 'he')
   * @param flow - Optional flow type. Defaults to current flow from URL
   */
  setLanguage: (value: string, flow?: FlowType) => setCookie(getFlowCookieName(COOKIE_BASE_NAMES.SELECTED_LANGUAGE, flow), value, { expires: 365 }),
  
  /**
   * Remove selected language for the specified flow
   * @param flow - Optional flow type. Defaults to current flow from URL
   */
  removeLanguage: (flow?: FlowType) => removeCookie(getFlowCookieName(COOKIE_BASE_NAMES.SELECTED_LANGUAGE, flow)),

  /**
   * Get cookie consent status (shared across all flows, no flow prefix)
   * @returns Cookie consent value or null if not found
   */
  getCookieConsent: () => getCookie(COOKIE_BASE_NAMES.COOKIE_CONSENT),
  
  /**
   * Set cookie consent status (shared across all flows, no flow prefix)
   * @param value - Consent value to store
   */
  setCookieConsent: (value: string) => setCookie(COOKIE_BASE_NAMES.COOKIE_CONSENT, value, { expires: 365 }),
  
  /**
   * Remove cookie consent status (shared across all flows, no flow prefix)
   */
  removeCookieConsent: () => removeCookie(COOKIE_BASE_NAMES.COOKIE_CONSENT),
};
